import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import FirmwareRolloutDetailDTO from '../../../types/firmware-rollout/FirmwareRolloutDetailDTO';
import FirmwareRolloutDTO from '../../../types/firmware-rollout/FirmwareRolloutDTO';
import FirmwareRolloutSampleEventDTO from '../../../types/firmware-rollout/FirmwareRolloutSampleEventDTO';
import RolloutGlobalSummaryDTO from '../../../types/firmware-rollout/RolloutGlobalSummaryDTO';
import RolloutSampleStatusDTO from '../../../types/firmware-rollout/RolloutSampleStatusDTO';
import RolloutStatus from '../../../types/firmware-rollout/RolloutStatus';
import RolloutStudioSummaryDTO from '../../../types/firmware-rollout/RolloutStudioSummaryDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class FirmwareRolloutService {

    private RESOURCE: string = 'firmware-rollout';

    constructor(private baseService: BaseService) {
    }

    /**
     * Rejects (including 404 when no rollout ever targeted the sample) - callers should treat any
     * rejection as "no data".
     *
     * @param physicalSampleId The physical sample to look up.
     * @return The sample's current rollout status.
     */
    public async getSampleStatus(physicalSampleId: number): Promise<RolloutSampleStatusDTO> {
        const result = await this.baseService.get<any>(this.RESOURCE + '/sample/' + physicalSampleId);
        return new RolloutSampleStatusDTO(result);
    }

    /**
     * Batched form of getSampleStatus - samples with no rollout history simply don't appear in the result.
     *
     * @param physicalSampleIds The physical samples to look up.
     * @return The current rollout status of every sample that has ever been part of a rollout.
     */
    public async getSampleStatuses(physicalSampleIds: number[]): Promise<RolloutSampleStatusDTO[]> {
        if (physicalSampleIds.length === 0) {
            return [];
        }
        let params = new HttpParams();
        physicalSampleIds.forEach(id => {
            params = params.append('physicalSampleId', id.toString());
        });
        const result = await this.baseService.get<any[]>(this.RESOURCE + '/sample/batch', params);
        return result.map(status => new RolloutSampleStatusDTO(status));
    }

    public async getStudioSummary(studioId: number, firmwareRevisionId?: number): Promise<RolloutStudioSummaryDTO> {
        let params = new HttpParams();
        if (firmwareRevisionId != null) {
            params = params.set('firmwareRevisionId', firmwareRevisionId.toString());
        }
        const result = await this.baseService.get<any>(this.RESOURCE + '/studio/' + studioId + '/summary', params);
        return new RolloutStudioSummaryDTO(result);
    }

    public async getGlobalSummary(): Promise<RolloutGlobalSummaryDTO> {
        const result = await this.baseService.get<any>(this.RESOURCE + '/summary');
        return new RolloutGlobalSummaryDTO(result);
    }

    /**
     * Always sorted newest-first.
     *
     * @param page The page number to fetch.
     * @param size The number of elements per page.
     * @param studioId Optional studio filter.
     * @param statuses Optional rollout statuses to filter by.
     * @return The paginated rollout history.
     */
    public async getRollouts(page: number = 0, size: number = 10,
                             studioId?: number, statuses?: RolloutStatus[]): Promise<Page<FirmwareRolloutDTO>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString())
            .set('sort', 'triggeredAt,desc');
        if (studioId != null) {
            params = params.set('studioId', studioId.toString());
        }
        if (statuses != null) {
            statuses.forEach(status => {
                params = params.append('status', status);
            });
        }
        const result = await this.baseService.get<Page<any>>(this.RESOURCE, params);
        return {
            content: result.content.map(rollout => new FirmwareRolloutDTO(rollout)),
            page: result.page
        };
    }

    public async getRolloutDetail(rolloutId: number): Promise<FirmwareRolloutDetailDTO> {
        const result = await this.baseService.get<any>(this.RESOURCE + '/' + rolloutId);
        return new FirmwareRolloutDetailDTO(result);
    }

    public async getSampleHistory(rolloutId: number,
                                  physicalSampleId: number): Promise<FirmwareRolloutSampleEventDTO[]> {
        const result = await this.baseService.get<any[]>(
            this.RESOURCE + '/' + rolloutId + '/sample/' + physicalSampleId + '/history');
        return result.map(event => new FirmwareRolloutSampleEventDTO(event));
    }

    /**
     * Only stops future batches from being dispatched - a batch already broadcast to samples can't be
     * recalled, so those samples still report their real outcome after the rollout is cancelled.
     *
     * @param rolloutId The rollout to cancel.
     */
    public async cancelRollout(rolloutId: number): Promise<void> {
        await this.baseService.post<void>(this.RESOURCE + '/' + rolloutId + '/cancel', {});
    }

}
